// Round-robin schedule generation via the classic "circle method".
//
// ONE implementation for both consumers (INT-0043): the admin's scoresheet /
// Auto-Populate (`apps/admin/src/utils/scoresheets/matchups.ts`) and the API's
// sign-up wizard draft schedule (`organization.controller.ts:onboardingTeams`).
// They must never disagree on pairings or round numbering, because the wizard's
// games are stamped like Auto-Populate output and Auto-Populate's re-run
// replaces them.
//
// For N teams (seeds 1..N): if N is odd, a BYE (0) is added so every round is
// fully paired; results in (N-1) rounds with floor(N/2) matches per round, every
// pair meeting exactly once. `roundsPerMatchup` repeats the whole cycle.

export interface RoundRobinMatch {
  home: number; // 1-indexed seed
  away: number; // 1-indexed seed
}

export interface RoundRobinRound {
  round: number;
  matches: RoundRobinMatch[];
}

export interface RoundRobinSchedule {
  rounds: RoundRobinRound[];
  courtsPerRound: number;
}

export function generateRoundRobinSchedule(
  numTeams: number,
  roundsPerMatchup = 1,
): RoundRobinSchedule {
  if (!numTeams || numTeams < 2) {
    return { rounds: [], courtsPerRound: 0 };
  }

  const seeds: number[] = [];
  for (let i = 1; i <= numTeams; i += 1) seeds.push(i);
  if (numTeams % 2 === 1) seeds.push(0); // 0 = BYE

  const n = seeds.length;
  const roundsCount = n - 1;
  const half = n / 2;
  const arr = seeds.slice();

  const baseRounds: RoundRobinMatch[][] = [];
  for (let r = 0; r < roundsCount; r += 1) {
    const matches: RoundRobinMatch[] = [];
    for (let i = 0; i < half; i += 1) {
      const a = arr[i];
      const b = arr[n - 1 - i];
      if (a !== 0 && b !== 0) matches.push({ home: a, away: b });
    }
    baseRounds.push(matches);
    // Rotate: keep the first seed fixed, rotate the rest clockwise.
    const fixed = arr[0];
    const rest = arr.slice(1);
    rest.unshift(rest.pop() as number);
    arr.splice(0, arr.length, fixed, ...rest);
  }

  const repeats = Math.max(1, roundsPerMatchup || 1);
  const rounds: RoundRobinRound[] = [];
  let roundNum = 1;
  for (let m = 0; m < repeats; m += 1) {
    baseRounds.forEach((matches) => {
      // Rotate game order per round so no team (especially the pinned seed 1,
      // whose pairing the circle method always emits FIRST) sits in the same
      // position every round — court = position-in-round downstream (the
      // scoresheet's rounds-table rows and Auto-Populate's idx % locations).
      // Rotate into a NEW array (base arrays are shared across repeats) and key
      // off the FINAL round number so a double round-robin keeps rotating.
      const shift = matches.length ? (roundNum - 1) % matches.length : 0;
      const rotated = matches.slice(shift).concat(matches.slice(0, shift));
      rounds.push({ round: roundNum, matches: rotated });
      roundNum += 1;
    });
  }

  return { rounds, courtsPerRound: Math.floor(numTeams / 2) };
}

export interface RoundRobinMatchup {
  round: number; // 1-indexed
  gameNumber: number; // 1-indexed, restarts every round (how schedulers key slots)
  homeTeamId: string;
  awayTeamId: string;
}

/**
 * Concrete team-id pairings for an ordered team list (index = seed − 1).
 * `gameNumber` restarts at 1 in every round — that is how the schedulers match
 * a game to its grid slot (`getSlotGame` keys on round + gameNumber), and what
 * Auto-Populate stamps into `competitionMeta`.
 */
export function buildRoundRobinMatchups(
  teamIds: string[],
  roundsPerMatchup = 1,
): RoundRobinMatchup[] {
  const ids = teamIds.filter((id) => !!id);
  const schedule = generateRoundRobinSchedule(ids.length, roundsPerMatchup);
  const out: RoundRobinMatchup[] = [];
  schedule.rounds.forEach(({ round, matches }) => {
    matches.forEach((m, idx) => {
      out.push({
        round,
        gameNumber: idx + 1,
        homeTeamId: ids[m.home - 1],
        awayTeamId: ids[m.away - 1],
      });
    });
  });
  return out;
}
